import { useState, useEffect, useMemo } from "react";
import { useForm } from "@mantine/form";
import { Button, Group, Text, Tabs, Menu, ActionIcon } from "@mantine/core";
import { IconDotsVertical } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import _ from "lodash";
import CommissionSettingScheduleCard from "./commissionSettingScheduleCard";
import CommissionSettingOverridingCard from "./commissionSettingOverridingCard";
import CommissionSettingFixAmountSchedule from "./commissionSettingFixAmountSchedule";
import { pickCommissionAffectingSnapshot } from "../helpers/commissionOrderChangeUtils";
import { buildEmptyRateObject } from "../helpers/commissionScheduleYears";

import ReactJson from "react-json-view";

const OrderCommissionSetting = ({ form, onCommissionPaymentsRefresh }) => {
  const orderId = form.values._id;
  const commissionSettingId = _.get(form.values, "commissionSetting");
  const [commissionSetting, setCommissionSetting] = useState(null);
  const [scheduleMode, setScheduleMode] = useState("view");
  const [overrideMode, setOverrideMode] = useState("view");
  const [api] = useServerApi();

  const commissionWatchKey = useMemo(
    () => JSON.stringify(pickCommissionAffectingSnapshot(form.values)),
    [form.values]
  );

  const fetchCommissionSetting = async () => {
    try {
      const data = await api.getById({
        apiEntity: "commissionSetting",
        id: commissionSettingId,
      });
      setCommissionSetting(data);
    } catch (error) {
      console.error("Error fetching commission setting:", error);
    }
  };

  useEffect(() => {
    fetchCommissionSetting();
  }, [commissionSettingId, commissionWatchKey]);

  //Get the commission schedule for the order
  const getOrderCommissionSchedule = async () => {
    try {
      const data = await api.CommissionServer.getOrderCommissionSchedule(
        orderId
      );
      if (data.commissionSetting) {
        form.setFieldValue("commissionSetting", data.commissionSetting);
      }
      await fetchCommissionSetting();
      setScheduleMode("view");

      await form.save();
    } catch (error) {
      console.error("Error getting order commission schedule:", error);
    } finally {
      onCommissionPaymentsRefresh?.();
    }
  };

  //Get the sales overriding for the order
  const resetCommissionSettingOverride = async () => {
    try {
      await api.CommissionServer.resetCommissionSettingOverride(orderId);
      await fetchCommissionSetting();
    } catch (error) {
      console.error("Error resetting commission setting override:", error);
    } finally {
      onCommissionPaymentsRefresh?.();
    }
  };

  //Customize the commission schedule
  const onCustomizeCommissionSchedule = async () => {
    // console.log("onCustomize", commissionSetting);
    try {
      if (!commissionSetting?.row) {
        commissionSetting.row = {
          grossRate: buildEmptyRateObject(),
        };
      }
      let row = commissionSetting?.row;
      row.isCustomized = true;

      console.log("onCustomizeCommissionSchedule > row", row);
      const data = await api.updateById({
        apiEntity: "commissionSetting",
        id: commissionSettingId,
        values: {
          row: row,
        },
      });

      // console.log("onCustomizeCommissionSchedule", data);
      setScheduleMode("edit");
      await fetchCommissionSetting();
    } catch (error) {
      console.error("Error customizing commission schedule:", error);
    }
  };

  const onCustomizeOverriding = async () => {
    // console.log("onCustomizeOverriding", commissionSetting);
    const data = await api.updateById({
      apiEntity: "commissionSetting",
      id: commissionSettingId,
      values: { isCustomizedOverriding: true },
    });
    fetchCommissionSetting();
    setOverrideMode("edit");
  };

  const onUpdateCommissionSettingOverride = async () => {
    try {
      console.log(
        "onUpdateCommissionSettingOverride > commissionSetting",
        commissionSetting
      );
      await api.updateById({
        apiEntity: "commissionSetting",
        id: commissionSettingId,
        values: commissionSetting,
      });
      const result = await api.CommissionServer.updateCommissionSettingOverride(
        orderId
      );
      console.log("onUpdateCommissionSettingOverride > result", result);
      await fetchCommissionSetting();
      setOverrideMode("view");
      await form.save();
    } catch (error) {
      console.error("Error recalculating sales overriding:", error);
    } finally {
      onCommissionPaymentsRefresh?.();
    }
  };

  const saveCommissionSetting = async () => {
    try {
      await api.updateById({
        apiEntity: "commissionSetting",
        id: commissionSettingId,
        values: commissionSetting,
      });
      await fetchCommissionSetting();
      setScheduleMode("view");
      await form.save();
      await fetchCommissionSetting();
    } catch (error) {
      console.error("Error saving commission setting:", error);
    } finally {
      onCommissionPaymentsRefresh?.();
    }
  };

  return (
    <>
      {/* {form.values._id} */}
      {/* {mode} */}
      {/* <ReactJson
        src={commissionSetting}
        collapsed={true}
        style={{ background: "white" }}
      />
      {scheduleMode} */}
      {commissionSetting?.isFixedCommission && (
        <CommissionSettingFixAmountSchedule
          commissionSetting={commissionSetting}
          onRefresh={fetchCommissionSetting}
        />
      )}

      {!commissionSetting?.isFixedCommission && (
        <CommissionSettingScheduleCard
          commissionSetting={commissionSetting}
          product={form.values?.product}
          onReset={getOrderCommissionSchedule}
          onCustomize={onCustomizeCommissionSchedule}
          isCustomized={commissionSetting?.isCustomized}
          isCertain={form.values?.isCertainPayment ?? false}
          mode={scheduleMode}
          onRefresh={fetchCommissionSetting}
        />
      )}
      <Group>
        {
          // (commissionSetting?.row?.isCustomized ||
          //   commissionSetting?.isCustomizedOverriding) &&
          scheduleMode == "edit" && (
            <Button onClick={saveCommissionSetting} size="xs">
              Save
            </Button>
          )
        }
      </Group>
      <CommissionSettingOverridingCard
        commissionSetting={commissionSetting}
        onResetOverride={resetCommissionSettingOverride}
        onCustomize={onCustomizeOverriding}
        onUpdateOverride={onUpdateCommissionSettingOverride}
        isCustomized={commissionSetting?.isCustomizedOverriding}
        mode={overrideMode}
      />
    </>
  );
};

export default OrderCommissionSetting;
